import {
    Button,
    Modal,
    InputDeprecated,
    InfoCard,
    InlineAlert,
} from '@/components';
import { useForm } from 'react-hook-form';
import { R } from '@/features/programs/resources/ProgramsResources.resx';
import { format as formatString } from '@/Resources.resx';
import { calculateMinimalWeight } from '@/shared/helpers/calculateMinimalWeightBMI';
import { formatNumber } from '@/shared/helpers/formatNumber';
import { useAtom } from 'jotai/index';
import { updateNnSundVaegtProgramAtom } from '../model/updateNnSundVaegtProgramAtom';
import { useState } from 'react';

export type SetWeightGoalModalProps = {
    height: number;
    initialWeight: number;
    currentWeightGoal: number;
    onClose: () => void;
};

export type IFormSetWeightGoal = { weightGoal: number };

const isNumber = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;

    const n = Number(value);
    return Number.isFinite(n);
};

export const SetWeightGoalModal = (props: SetWeightGoalModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<IFormSetWeightGoal>({
        mode: 'onSubmit',
        defaultValues: { weightGoal: props.currentWeightGoal },
    });
    const [{ mutate, status, reset, error }] = useAtom(
        updateNnSundVaegtProgramAtom,
    );
    const [showErrors, setShowErrors] = useState(false);
    const weightGoal = watch('weightGoal');

    const hasData = props.height > 0 && props.initialWeight > 0;
    const minimalWeight = hasData ? calculateMinimalWeight(props.height) : 50;

    const showWeightTooLowInfo =
        hasData && isNumber(weightGoal) && weightGoal < minimalWeight;

    const onSubmit = (data: IFormSetWeightGoal) => {
        mutate(
            { DesiredWeightKg: data.weightGoal },
            {
                onSuccess: () => {
                    props.onClose();
                },
                onError: () => {
                    reset();
                },
            },
        );
    };

    const onError = () => {
        setShowErrors(true);
    };

    return (
        <Modal
            onClose={() => {
                reset();
                props.onClose();
            }}
            title={R.SundvaegtSetWeightGoalModalTitle}
        >
            {status !== 'error' && (
                <>
                    <p className="text-size-[18px] text-gray-900">
                        {R.SundvaegtSetWeightGoalModalDescription}
                    </p>
                    <form onSubmit={handleSubmit(onSubmit, onError)}>
                        <InputDeprecated
                            type="number"
                            step="0.1"
                            suffix="kg"
                            placeholder={
                                R.SundvaegtSetWeightGoalInputPlaceholder
                            }
                            label={R.SundvaegtSetWeightGoalModalInputLabel}
                            inputClassName={`${
                                showErrors && errors.weightGoal
                                    ? 'border border-secondary-red'
                                    : ''
                            }`}
                            containerClassName="mt-8"
                            {...register('weightGoal', {
                                required:
                                    R.SundvaegtSetWeightGoalModalInputRequiredLabel,
                                validate: {
                                    weightGoalLessThanInitialWeight: (
                                        value: number,
                                    ) => {
                                        if (
                                            props.initialWeight > 0 &&
                                            value > props.initialWeight
                                        ) {
                                            return R.SundvaegtSetWeightGoalModalValidationWeightGoalTooHigh;
                                        }
                                        return true;
                                    },
                                    weightGoalTooLow: (value: number) => {
                                        if (
                                            isNumber(value) &&
                                            value < minimalWeight
                                        ) {
                                            return formatString(
                                                R.SundvaegtSetWeightGoalModalValidationWeightGoalTooLow,
                                                formatNumber(minimalWeight),
                                            ).toString();
                                        }
                                        return true;
                                    },
                                },
                                onChange: () => setShowErrors(false),
                            })}
                            error={
                                showErrors
                                    ? errors.weightGoal?.message
                                    : undefined
                            }
                        />

                        {showWeightTooLowInfo && showErrors && (
                            <InfoCard className="mt-4">
                                <h3 className="text-primary-blue font-size-[18px] font-bold">
                                    {R.SundvaegtSetWeightGoalModalInfoTitle}
                                </h3>
                                <p className="font-size-[18px] text-gray-900">
                                    {formatString(
                                        R.SundvaegtSetWeightGoalModalInfoDescription,
                                        formatNumber(minimalWeight),
                                    )}
                                </p>
                            </InfoCard>
                        )}

                        <div className="mt-8 flex w-full flex-row justify-end">
                            <Button variant="primary" type="submit">
                                {R.SundvaegtSetWeightGoalModalSubmitButton}
                            </Button>
                        </div>
                    </form>
                </>
            )}

            {status === 'error' && (
                <InlineAlert onClose={reset} errorMessage={error?.message} />
            )}
        </Modal>
    );
};
